/**
 * Hover-intent navigation prefetch. Warms the target of any element carrying a
 * `data-prefetch` attribute when the pointer dwells on it, so a subsequent click
 * navigates near-instantly.
 *
 * Listeners are attached per link via `pointerenter` (which does not bubble), so
 * there is no document-wide pointer monitoring — nothing runs while the pointer
 * is anywhere other than a tagged link. Links added later by the theme editor are
 * picked up on `shopify:section:load`.
 *
 * URL:    the element's `data-prefetch` value, or its `href` when the attribute
 *         is present but empty. Only same-origin URLs are prefetched, once each.
 * Method: Speculation Rules API — its cache is reused for Shopify's no-store
 *         storefront HTML — with a <link rel="prefetch"> fallback.
 *
 * Opt a link in by adding `data-prefetch`; override the dwell per link with
 * `data-prefetch-delay` (ms).
 *
 * @example
 *   <a href='/collections/tees' data-prefetch>Tees</a>
 *   <a href='/pages/lookbook' data-prefetch data-prefetch-delay='150'>Lookbook</a>
 */
const DEFAULT_DELAY = 300;

const prefetched = new Set();
const bound = new WeakSet();
const supportsSpeculationRules =
  typeof HTMLScriptElement !== 'undefined' && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules');

function prefetch(url) {
  let target;
  try {
    target = new URL(url, window.location.href);
  } catch {
    return;
  }
  if (target.origin !== window.location.origin || prefetched.has(target.href)) return;
  prefetched.add(target.href);

  if (supportsSpeculationRules) {
    const rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls: [target.href] }] });
    document.body.appendChild(rules);
  } else {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = target.href;
    document.head.appendChild(link);
  }
}

function wire() {
  document.querySelectorAll('[data-prefetch]').forEach((link) => {
    if (bound.has(link)) return;
    bound.add(link);

    let timer;
    link.addEventListener('pointerenter', () => {
      const url = link.dataset.prefetch || link.href;
      if (!url) return;
      const delay = parseInt(link.dataset.prefetchDelay, 10) || DEFAULT_DELAY;
      timer = setTimeout(() => prefetch(url), delay);
    });
    link.addEventListener('pointerleave', () => clearTimeout(timer));
  });
}

wire();
// Theme editor re-renders sections without a full page load — re-scan for new links.
document.addEventListener('shopify:section:load', wire);
